import { Command, Element, Text } from "ckeditor5";

import { replaceModelItem } from "../../../util/document";
import { LATEX_REGEX } from "../plugin";

type TextOrLatex =
  | string
  | { latex: string; text: string; element: "latex-block" | "latex-inline" };

export default class RecognizeLatexCommand extends Command {
  execute() {
    const root = this.editor.model.document.getRoot();
    if (root == null) return;
    const replacements = this.findLatexReplacements(root);

    if (replacements.length === 0) return;

    this.editor.model.change((writer) => {
      for (const replacement of replacements) {
        const replaceWith = replacement.replaceWith.map((r) => {
          if (typeof r === "string") return writer.createText(r);
          const element = writer.createElement(r.element, {
            latex: r.latex,
          });
          writer.append(writer.createText(r.text), element);
          return element;
        });

        replaceModelItem(replacement.originalText, replaceWith, writer);
      }
    });
  }

  private findLatexReplacements(element: Element) {
    const results: Array<{
      originalText: Text;
      replaceWith: TextOrLatex[];
    }> = [];

    for (const child of element.getChildren()) {
      if (child.is("model:$text")) {
        const splitted = this.splitByLatexPattern(child.data);
        if (!splitted?.length) {
          continue;
        }
        results.push({ originalText: child, replaceWith: splitted });
      }

      if (child.is("model:element") && !child.name.startsWith("latex-")) {
        results.push(...this.findLatexReplacements(child));
      }
    }

    return results;
  }

  splitByLatexPattern(text: string) {
    const results: TextOrLatex[] = [];

    const matches = Array.from(text.matchAll(LATEX_REGEX));

    if (matches.length === 0) return;

    let lastIndex = 0;

    for (const match of matches) {
      if (match.index == null) continue;
      if (match.index > lastIndex) {
        results.push(text.substring(lastIndex, match.index));
      }

      const latex = match.groups?.inline ?? match.groups?.block;
      //
      // One of those groups will match, but typescript doesn't know that
      //
      if (latex == null) continue;

      results.push({
        latex,
        text: match[0],
        element: match.groups?.block ? "latex-block" : "latex-inline",
      });

      lastIndex += match.index + match[0].length;
    }

    if (lastIndex < text.length) {
      results.push(text.substring(lastIndex));
    }

    return results;
  }
}
